import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkConnectedOverlay, ConnectedPosition, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { CoreDropdownOption, DropdownValue } from './dropdown.type';

@Component({
    selector: 'core-ui-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => DropdownComponent),
        multi: true,
    }],
})
export class DropdownComponent implements ControlValueAccessor, OnChanges {
    @ViewChild('valueEl', { static: false }) valueEl?: ElementRef<HTMLElement>;
    @ViewChild('panelEl', { read: ElementRef }) panelEl?: ElementRef<HTMLElement>;
    @ViewChild('originRef', { read: ElementRef }) originRef?: ElementRef<HTMLElement>;
    @ViewChild('connectedDrop', { static: false }) connectedDrop?: CdkConnectedOverlay;

    @Input() label: string = '';

    @Input() placeholder: string = 'Selecciona uno';

    @Input() options: CoreDropdownOption[] = [];

    @Input() disabled: boolean = false;

    @Input() error: boolean = false;

    @Input() readOnly: boolean = false;

    @Input() hideChevron: boolean = false;

    @Input() size: 'sm' | 'md' = 'md';

    @Input() sizeIcon: 'sm' | 'md' | 'base' | 'lg' = 'md';

    @Input() iconColor: string = '#5f7081';
    @Input() iconLeft?: string;
    @Input() iconRight?: string;

    @Input() optionLabelKey: 'label' = 'label';
    @Input() optionValueKey: 'value' = 'value';
    @Input() maxVisibleSelected = 3;

    @Input() valueTpl?: TemplateRef<any>;
    @Input() optionTpl?: TemplateRef<any>;
    @Input() uiMode: 'form' | 'filter' = 'form';
    @Input() readOnlyOptions = false;
    @Input() openDisplayText?: string;

    @Input() multiple = false;

    @Input() showApplyButton = false;

    @Input() forceOpenDown = false;

    @Input() useOverlay = true;

    @Output() valueChange = new EventEmitter<string>();

    @Output() valueChangeMulti = new EventEmitter<string[]>();

    isOpen = false;
    private static openedRef: DropdownComponent | null = null;
    openUp = false;
    panelMaxHeight = 220;

    private _value: DropdownValue = null;

    private _draftValue: DropdownValue = null;

    private ignoreNextDocDown = false;
    private readonly dropdownPreferredMaxHeight = 220;
    private readonly dropdownMinHeight = 120;
    private readonly viewportBuffer = 8;

    private ro?: ResizeObserver;
    displayText = '';
    private toggleStartedByPointer = false;
    activeOptionIndex = -1;
    isPanelPositioned = true;

    readonly overlayScrollStrategy: ScrollStrategy;
    readonly overlayPositions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
    ];
    readonly overlayPanelClass = 'core-ui-dropdown-overlay-pane';
    overlayWidth = 0;

    @Input()
    get value(): DropdownValue {
        return this._value;
    }
    set value(val: DropdownValue) {
        this._value = this.normalizeValue(val);
    }

    private onChange: (value: DropdownValue) => void = () => { };
    private onTouched: () => void = () => { };

    private overlayFollowTeardown?: () => void;
    private pageScrollBlockTeardown?: () => void;
    private overlayPositionRaf = 0;

    constructor(
        private elementRef: ElementRef,
        private overlay: Overlay,
        private changeDetector: ChangeDetectorRef,
    ) {
        this.overlayScrollStrategy = this.overlay.scrollStrategies.reposition({ scrollThrottle: 16 });
    }

    onPanelMouseDown(): void {
        this.ignoreNextDocDown = true;
        queueMicrotask(() => (this.ignoreNextDocDown = false));
    }

    onControlPointerDown(): void {
        this.toggleStartedByPointer = true;
    }

    onOptionMouseEnter(index: number): void {
        if (this.hasSelectedValue) {
            this.activeOptionIndex = this.getSelectedOptionIndex();
            return;
        }

        this.activeOptionIndex = index;
    }

    onOverlayDetach(): void {
        if (this.isOpen) this.close();
    }

    private updateOverlayWidth(): void {
        if (!this.useOverlay) return;
        const w = this.originRef?.nativeElement?.getBoundingClientRect().width ?? 0;
        this.overlayWidth = w > 0 ? w : 200;
    }

    @HostListener('document:mousedown', ['$event'])
    onDocMouseDown(ev: MouseEvent): void {
        if (!this.isOpen) return;
        if (this.ignoreNextDocDown) return;
        const t = ev.target as Node;
        const host = this.elementRef.nativeElement as HTMLElement;
        if (host.contains(t)) return;
        if (this.useOverlay) {
            const pane = document.querySelector(`.${this.overlayPanelClass}`);
            if (pane?.contains(t)) return;
        }
        this.close();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        if (!this.isOpen) return;
        this.scheduleOverlayGeometrySync();
    }

    ngAfterViewInit(): void {
        this.setupResizeObserver();
        this.refreshDisplayText();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['multiple']) {
            this._value = this.normalizeValue(this._value);
        }

        if (changes['value'] || changes['multiple']) {
            this._draftValue = this.cloneValue(this._value);
        }

        if (changes['options'] && this.activeOptionIndex >= this.options.length) {
            this.activeOptionIndex = -1;
        }

        this.refreshDisplayText();
    }

    ngOnDestroy(): void {
        this.ro?.disconnect();
        this.detachOverlayFollowListeners();
        this.unblockPageScroll();
        if (DropdownComponent.openedRef === this) DropdownComponent.openedRef = null;
    }

    close(): void {
        this.detachOverlayFollowListeners();
        this.unblockPageScroll();
        this.isOpen = false;
        this.activeOptionIndex = -1;
        if (DropdownComponent.openedRef === this) DropdownComponent.openedRef = null;
        this.onTouched();
    }

    private blockPageScroll(): void {
        if (typeof document === 'undefined') return;
        if (this.pageScrollBlockTeardown) return;

        const preventPageScroll = (event: Event) => {
            if (this.isEventInsideDropdownPanel(event)) return;
            event.preventDefault();
        };

        document.addEventListener('wheel', preventPageScroll, { capture: true, passive: false });
        document.addEventListener('touchmove', preventPageScroll, { capture: true, passive: false });

        this.pageScrollBlockTeardown = () => {
            document.removeEventListener('wheel', preventPageScroll, true);
            document.removeEventListener('touchmove', preventPageScroll, true);
            this.pageScrollBlockTeardown = undefined;
        };
    }

    private unblockPageScroll(): void {
        this.pageScrollBlockTeardown?.();
    }

    private isEventInsideDropdownPanel(event: Event): boolean {
        const target = event.target as Node | null;
        if (!target) return false;

        if (!this.useOverlay) {
            return !!this.panelEl?.nativeElement.contains(target);
        }

        const pane = document.querySelector(`.${this.overlayPanelClass}`);
        return !!pane?.contains(target);
    }

    private setupResizeObserver(): void {
        if (!this.valueEl?.nativeElement) return;

        this.ro = new ResizeObserver(() => this.refreshDisplayText());
        this.ro.observe(this.valueEl.nativeElement);
    }

    private refreshDisplayText(): void {
        if (!this.multiple) {
            this.displayText = this.getSelectedLabel();
            return;
        }

        requestAnimationFrame(() => {
            const selectedOpts = this.getSelectedOptionsForDisplay();
            const labels = selectedOpts.map(o => this.getOptionLabel(o));

            const maxWidth = this.getAvailableTextWidth();
            this.displayText = this.fitTextToWidth(labels, maxWidth);
        });
    }

    private getAvailableTextWidth(): number {
        const el = this.valueEl?.nativeElement;
        if (!el) return 0;
        return Math.max(el.clientWidth, 0);
    }

    private getSelectedOptionsForDisplay(): CoreDropdownOption[] {
        const selectedValues = this.asArray(this.showApplyButton ? this._draftValue : this._value);

        return this.options.filter(o => selectedValues.includes(this.getOptionValue(o)));
    }

    getSelectedDisplayText(): string {
        if (!this.multiple) return this.getSelectedLabel();

        const selectedOpts = this.getSelectedOptionsForDisplay();
        const labels = selectedOpts.map(o => this.getOptionLabel(o));

        if (labels.length === 0) return '';

        if (labels.length <= this.maxVisibleSelected) {
            return labels.join(', ');
        }

        const visible = labels.slice(0, this.maxVisibleSelected);
        const remaining = labels.length - this.maxVisibleSelected;

        return `${visible.join(', ')}… (+${remaining})`;
    }

    private fitTextToWidth(labels: string[], maxWidth: number): string {
        if (labels.length === 0) return '';
        if (maxWidth <= 0) return '';

        const host = this.valueEl?.nativeElement;
        if (!host) return labels.join(', ');

        const measurer = document.createElement('span');
        const styles = getComputedStyle(host);

        measurer.style.position = 'fixed';
        measurer.style.left = '-9999px';
        measurer.style.top = '-9999px';
        measurer.style.whiteSpace = 'nowrap';
        measurer.style.font = styles.font;
        document.body.appendChild(measurer);

        const measure = (text: string) => {
            measurer.textContent = text;
            return measurer.getBoundingClientRect().width;
        };

        const suffixFor = (remaining: number) => remaining > 0 ? `…(+${remaining})` : '';

        const full = labels.join(', ');
        if (measure(full) <= maxWidth) {
            document.body.removeChild(measurer);
            return full;
        }

        const visible: string[] = [];
        for (let i = 0; i < labels.length; i++) {
            const remaining = labels.length - (i + 1);
            const suffix = suffixFor(remaining);

            const core = [...visible, labels[i]].join(', ');
            const candidate = core + suffix;

            if (measure(candidate) <= maxWidth) {
                visible.push(labels[i]);
                continue;
            }

            const suffixW = suffix ? measure(suffix) : 0;
            const available = Math.max(maxWidth - suffixW, 0);

            const clippedCore = this.clipTextToWidth(core, available, measure);

            document.body.removeChild(measurer);

            let finalText = clippedCore + suffix;
            while (finalText.length > 1 && this.measureWithHost(finalText, host) > maxWidth) {
                const newCore = clippedCore.slice(0, Math.max(clippedCore.length - 1, 1));
                finalText = newCore + suffix;
                if (newCore.length <= 1) break;
            }

            if (!clippedCore) return labels[0].slice(0, 1) + suffix;
            return finalText;
        }

        document.body.removeChild(measurer);
        return labels[0].slice(0, 1) + suffixFor(labels.length - 1);
    }

    private clipTextToWidth(
        text: string,
        maxWidth: number,
        measure: (t: string) => number
    ): string {
        if (!text) return '';
        if (maxWidth <= 0) return '';

        if (measure(text) <= maxWidth) return text;

        let lo = 1;
        let hi = text.length;

        while (lo < hi) {
            const mid = Math.ceil((lo + hi) / 2);
            const candidate = text.slice(0, mid);
            if (measure(candidate) <= maxWidth) lo = mid;
            else hi = mid - 1;
        }

        return text.slice(0, Math.max(lo, 1));
    }

    private measureWithHost(text: string, host: HTMLElement): number {
        const measurer = document.createElement('span');
        const styles = getComputedStyle(host);

        measurer.style.position = 'fixed';
        measurer.style.left = '-9999px';
        measurer.style.top = '-9999px';
        measurer.style.whiteSpace = 'nowrap';
        measurer.style.font = styles.font;

        measurer.textContent = text;
        document.body.appendChild(measurer);
        const w = measurer.getBoundingClientRect().width;
        document.body.removeChild(measurer);
        return w;
    }

    writeValue(value: DropdownValue): void {
        this._value = this.normalizeValue(value);
        this._draftValue = this.cloneValue(this._value);
        this.refreshDisplayText();
    }

    registerOnChange(fn: (value: DropdownValue) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (isDisabled) {
            this.detachOverlayFollowListeners();
            this.unblockPageScroll();
            this.isOpen = false;
            if (DropdownComponent.openedRef === this) DropdownComponent.openedRef = null;
        }
    }

    private get isBlocked(): boolean {
        return this.disabled || this.readOnly;
    }

    toggleDropdown(): void {
        if (this.isBlocked) return;

        const nextOpen = !this.isOpen;
        const shouldBlurAfterPointerClose = !nextOpen && this.toggleStartedByPointer;

        if (nextOpen) {
            this.isPanelPositioned = false;
            if (DropdownComponent.openedRef && DropdownComponent.openedRef !== this) {
                DropdownComponent.openedRef.close();
            }
            DropdownComponent.openedRef = this;
            this.computeDropdownDirection();
            this.updateOverlayWidth();

            if (this.showApplyButton) {
                this._draftValue = this.cloneValue(this._value);
            }
        } else {
            if (DropdownComponent.openedRef === this) DropdownComponent.openedRef = null;
        }

        this.isOpen = nextOpen;
        if (this.isOpen) {
            this.blockPageScroll();
            this.setInitialActiveOption();
            if (this.useOverlay) {
                this.attachOverlayFollowListeners();
                setTimeout(() => this.scheduleOverlayGeometrySync(), 0);
            }
            this.onTouched();
        } else {
            this.detachOverlayFollowListeners();
            this.unblockPageScroll();
            this.activeOptionIndex = -1;
            if (shouldBlurAfterPointerClose) {
                this.originRef?.nativeElement.blur();
            }
        }

        this.toggleStartedByPointer = false;
    }

    onControlKeyDown(event: KeyboardEvent): void {
        if (this.isBlocked) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                event.stopPropagation();
                if (!this.isOpen) {
                    this.openDropdownFromKeyboard();
                    return;
                }
                this.moveActiveOption(1);
                return;

            case 'ArrowUp':
                event.preventDefault();
                event.stopPropagation();
                if (!this.isOpen) {
                    this.openDropdownFromKeyboard();
                    return;
                }
                this.moveActiveOption(-1);
                return;

            case 'Enter':
                if (!this.isOpen) return;
                event.preventDefault();
                event.stopPropagation();
                this.selectActiveOption();
                return;

            case 'Escape':
                if (!this.isOpen) return;
                event.preventDefault();
                event.stopPropagation();
                this.close();
                return;
        }
    }

    private openDropdownFromKeyboard(): void {
        if (DropdownComponent.openedRef && DropdownComponent.openedRef !== this) {
            DropdownComponent.openedRef.close();
        }

        DropdownComponent.openedRef = this;
        this.computeDropdownDirection();
        this.updateOverlayWidth();

        if (this.showApplyButton) {
            this._draftValue = this.cloneValue(this._value);
        }

        this.isOpen = true;
        this.isPanelPositioned = false;
        this.blockPageScroll();
        this.setInitialActiveOption();

        if (this.useOverlay) {
            this.attachOverlayFollowListeners();
            setTimeout(() => this.scheduleOverlayGeometrySync(), 0);
        }

        this.onTouched();
    }

    private setInitialActiveOption(): void {
        if (!this.options.length) {
            this.activeOptionIndex = -1;
            this.revealDropdownPanel();
            return;
        }

        const selectedIndex = this.getSelectedOptionIndex();
        this.activeOptionIndex = selectedIndex >= 0 ? selectedIndex : 0;
        this.scrollInitialOptionToTop();
    }

    private scrollInitialOptionToTop(): void {
        setTimeout(() => {
            requestAnimationFrame(() => {
                if (!this.isOpen) return;
                const container = this.getDropdownPanel();
                const activeOption = container?.querySelector<HTMLElement>('.core-filter-select__option--active');
                if (container && activeOption) {
                    container.scrollTop += activeOption.getBoundingClientRect().top - container.getBoundingClientRect().top;
                }
                this.revealDropdownPanel(container);
            });
        }, 0);
    }

    private getSelectedOptionIndex(): number {
        return this.options.findIndex((option) => this.isSelected(option));
    }

    private moveActiveOption(step: 1 | -1): void {
        if (!this.options.length) {
            this.activeOptionIndex = -1;
            return;
        }

        const currentIndex = this.activeOptionIndex >= 0 ? this.activeOptionIndex : 0;
        const nextIndex = (currentIndex + step + this.options.length) % this.options.length;
        const scrollBlock: ScrollLogicalPosition = step === -1 && currentIndex === 0
            ? 'end'
            : step === 1 && currentIndex === this.options.length - 1
                ? 'start'
                : 'nearest';

        this.activeOptionIndex = nextIndex;
        this.scrollActiveOptionIntoView(scrollBlock);
    }

    private selectActiveOption(): void {
        if (this.readOnlyOptions || this.activeOptionIndex < 0) return;

        const option = this.options[this.activeOptionIndex];
        if (!option) return;

        this.onSelect(option);
    }

    private scrollActiveOptionIntoView(block: ScrollLogicalPosition = 'nearest'): void {
        setTimeout(() => {
            requestAnimationFrame(() => {
                const container = this.getDropdownPanel();
                const activeOption = container?.querySelector<HTMLElement>('.core-filter-select__option--active');
                if (container && activeOption) {
                    const containerRect = container.getBoundingClientRect();
                    const optionRect = activeOption.getBoundingClientRect();
                    const isVisible =
                        optionRect.top >= containerRect.top && optionRect.bottom <= containerRect.bottom;

                    if (!isVisible) {
                        activeOption.scrollIntoView({ block, behavior: 'auto' });
                    }
                }
                this.revealDropdownPanel(container);
            });
        }, 0);
    }

    private revealDropdownPanel(container = this.getDropdownPanel()): void {
        this.isPanelPositioned = true;
        container?.classList.remove('is-positioning');
        this.changeDetector.markForCheck();
    }

    private getDropdownPanel(): HTMLElement | null {
        return this.useOverlay
            ? document.querySelector<HTMLElement>(`.${this.overlayPanelClass} .core-filter-select__dropdown`)
            : this.panelEl?.nativeElement ?? null;
    }

    private attachOverlayFollowListeners(): void {
        this.detachOverlayFollowListeners();

        const cleanups: Array<() => void> = [];
        const onFollow = () => this.queueOverlayPositionSync();

        const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
        if (vv) {
            vv.addEventListener('resize', onFollow);
            vv.addEventListener('scroll', onFollow);
            cleanups.push(() => {
                vv.removeEventListener('resize', onFollow);
                vv.removeEventListener('scroll', onFollow);
            });
        }

        if (typeof document !== 'undefined') {
            document.addEventListener('scroll', onFollow, true);
            cleanups.push(() => document.removeEventListener('scroll', onFollow, true));
        }

        this.overlayFollowTeardown = () => {
            cleanups.forEach((fn) => fn());
            this.cancelQueuedOverlayPositionSync();
            this.overlayFollowTeardown = undefined;
        };
    }

    private detachOverlayFollowListeners(): void {
        this.overlayFollowTeardown?.();
    }

    private cancelQueuedOverlayPositionSync(): void {
        if (this.overlayPositionRaf) {
            cancelAnimationFrame(this.overlayPositionRaf);
            this.overlayPositionRaf = 0;
        }
    }

    private queueOverlayPositionSync(): void {
        if (!this.useOverlay || !this.isOpen) return;
        if (this.overlayPositionRaf) return;
        this.overlayPositionRaf = requestAnimationFrame(() => {
            this.overlayPositionRaf = 0;
            this.computeDropdownDirection();
            this.updateOverlayWidth();
            this.connectedDrop?.overlayRef?.updatePosition();
            this.changeDetector.markForCheck();
        });
    }

    private scheduleOverlayGeometrySync(): void {
        if (!this.useOverlay || !this.isOpen) return;
        this.computeDropdownDirection();
        this.updateOverlayWidth();
        queueMicrotask(() => {
            requestAnimationFrame(() => {
                this.computeDropdownDirection();
                this.updateOverlayWidth();
                this.connectedDrop?.overlayRef?.updatePosition();
                // Dentro de un padre OnPush este callback asíncrono no se revisa solo.
                this.changeDetector.markForCheck();
            });
        });
    }

    private computeDropdownDirection(): void {
        if (this.forceOpenDown) {
            this.openUp = false;
            this.panelMaxHeight = this.dropdownPreferredMaxHeight;
            return;
        }
        const hostEl = this.elementRef.nativeElement as HTMLElement;
        const rect = hostEl.getBoundingClientRect();

        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const minSpaceToOpenDown = this.dropdownPreferredMaxHeight + this.viewportBuffer;

        this.openUp = spaceBelow < minSpaceToOpenDown && spaceAbove > spaceBelow;

        const chosenSideSpace = this.openUp ? spaceAbove : spaceBelow;
        const computed = Math.floor(chosenSideSpace - this.viewportBuffer);
        this.panelMaxHeight = Math.max(
            this.dropdownMinHeight,
            Math.min(this.dropdownPreferredMaxHeight, computed),
        );
    }

    onSelect(opt: CoreDropdownOption): void {
        if (this.isBlocked) return;
        const optValue = this.getOptionValue(opt);

        if (!this.multiple) {
            const next = optValue;
            const changed = next !== this._value;
            this._value = next;

            if (changed) {
                this.onChange(next);
                this.valueChange.emit(next);
            }
            this.onTouched();

            this.detachOverlayFollowListeners();
            this.unblockPageScroll();
            this.isOpen = false;
            this.activeOptionIndex = -1;
            if (DropdownComponent.openedRef === this) DropdownComponent.openedRef = null;
            return;
        }

        const base = this.showApplyButton ? this._draftValue : this._value;
        const current = this.asArray(base);

        const exists = current.includes(optValue);
        const nextArr = exists ? current.filter(v => v !== optValue) : [...current, optValue];

        if (this.showApplyButton) {
            this._draftValue = nextArr;
            this.refreshDisplayText();
            return;
        }

        this._value = nextArr;
        this.onChange(nextArr);
        this.onTouched();

        this.valueChangeMulti.emit(nextArr);

        this.refreshDisplayText();
    }

    apply(): void {
        if (!this.multiple || !this.showApplyButton) return;

        const next = this.asArray(this._draftValue);
        this._value = next;

        this.onChange(next);
        this.onTouched();

        this.valueChangeMulti.emit(next);

        this.detachOverlayFollowListeners();
        this.unblockPageScroll();
        this.isOpen = false;
        this.activeOptionIndex = -1;
        if (DropdownComponent.openedRef === this) DropdownComponent.openedRef = null;
        this.refreshDisplayText();
    }

    clear(): void {
        if (!this.multiple) {
            this._value = null;
            this.onChange(null);
            this.valueChange.emit('');
            return;
        }

        const empty: string[] = [];
        if (this.showApplyButton) {
            this._draftValue = empty;
        } else {
            this._value = empty;
            this.onChange(empty);
            this.valueChangeMulti.emit(empty);
        }
        this.refreshDisplayText();
    }

    getOptionLabel(opt: CoreDropdownOption): string {
        return typeof opt === 'string' ? opt : (opt.label ?? opt.value);
    }

    getOptionValue(opt: CoreDropdownOption): string {
        return typeof opt === 'string' ? opt : opt.value;
    }

    isSelected(opt: CoreDropdownOption): boolean {
        const v = this.getOptionValue(opt);

        if (!this.multiple) {
            return v === (this._value ?? '');
        }

        const base = this.showApplyButton ? this._draftValue : this._value;
        return this.asArray(base).includes(v);
    }

    getSelectedOption() {
        if (this.multiple) return null;
        if (this._value === null || this._value === undefined || this._value === '') return null;

        const value = String(this._value);
        return this.options.find(o => this.getOptionValue(o) === value) ?? null;
    }

    getSelectedLabel(): string {
        if (!this.multiple) {
            const opt = this.getSelectedOption();
            return opt ? this.getOptionLabel(opt) : '';
        }

        const base = this.showApplyButton ? this._draftValue : this._value;
        const selected = this.asArray(base);

        if (!selected.length) return '';
        if (selected.length === 1) {
            const opt = this.options.find(o => this.getOptionValue(o) === selected[0]);
            return opt ? this.getOptionLabel(opt) : selected[0];
        }
        return `${selected.length} seleccionados`;
    }

    get isFilled(): boolean {
        if (this.uiMode !== 'form') return false;

        if (!this.multiple) {
            return (typeof this._value === 'string' ? this._value : '').trim().length > 0;
        }

        const base = this.showApplyButton ? this._draftValue : this._value;
        return this.asArray(base).length > 0;
    }

    get hasSelectedValue(): boolean {
        const base = this.showApplyButton ? this._draftValue : this._value;
        return this.asArray(base).length > 0;
    }

    private asArray(v: DropdownValue): string[] {
        if (Array.isArray(v)) return v.filter(Boolean);
        if (typeof v === 'string' && v.trim().length) return [v];
        return [];
    }

    private normalizeValue(v: DropdownValue): DropdownValue {
        if (!this.multiple) {
            if (Array.isArray(v)) return v[0] ?? null;
            return (v ?? null) as string | null;
        }
        if (Array.isArray(v)) return v;
        if (typeof v === 'string' && v.trim().length) return [v];
        return [];
    }

    private cloneValue(v: DropdownValue): DropdownValue {
        return Array.isArray(v) ? [...v] : v;
    }

    get currentMultiValues(): string[] {
        return this.asArray(this.showApplyButton ? this._draftValue : this._value);
    }
}
